'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { sanitizeFirestoreData } from '@/lib/utils';
import type { Banda, SocialPost } from '@/lib/types';
import { RichText } from '@/components/ui/rich-text';
import Image from '@/components/Image';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Loader2, ArrowLeft, Music2, Users, Calendar, ListMusic, Play, Pause, Search, Newspaper,
  DoorOpen, Drum, ExternalLink,
} from 'lucide-react';

function hexToRgba(hex: string, alpha: number) {
  const h = (hex || '#9b8854').replace('#', '');
  if (h.length < 6) return `rgba(155,136,84,${alpha})`;
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${alpha})`;
}

/** Resultado de buscar una marcha en iTunes (única fuente con preview y CORS). */
type Pista = { previewUrl: string; artwork: string; artista: string; itunesUrl: string } | null;

function StatCard({ icon, value, label, color }: { icon: React.ReactNode; value: string | number; label: string; color: string }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-card/40 p-4 text-center">
      <div className="mx-auto mb-2 grid h-9 w-9 place-items-center rounded-xl" style={{ background: hexToRgba(color, 0.15), color }}>
        {icon}
      </div>
      <p className="text-lg font-black leading-none">{value}</p>
      <p className="mt-1 text-[0.65rem] uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}

/**
 * Fila del repertorio con reproducción bajo demanda.
 *
 * El repertorio se guarda solo como texto (nombre de la marcha), así que la
 * pista se busca en la **iTunes Search API** al pulsar play — es la única
 * fuente gratuita, con CORS y con preview. Limitación honesta: la preview es
 * de ~30 s y hay marchas cofrades que sencillamente no están en iTunes.
 */
function MarchaRow({
  nombre,
  index,
  color,
  reproduciendo,
  onPlay,
  onStop,
}: {
  nombre: string;
  index: number;
  color: string;
  reproduciendo: boolean;
  onPlay: (nombre: string, pista: Pista) => void;
  onStop: () => void;
}) {
  const [buscando, setBuscando] = useState(false);
  const [pista, setPista] = useState<Pista>(null);
  const [sinResultado, setSinResultado] = useState(false);

  const pulsar = async () => {
    if (reproduciendo) { onStop(); return; }
    if (pista) { onPlay(nombre, pista); return; }
    if (sinResultado) return;

    setBuscando(true);
    try {
      const url = `https://itunes.apple.com/search?term=${encodeURIComponent(nombre)}&media=music&entity=song&limit=3`;
      const r = await fetch(url);
      const j = await r.json();
      const hit = (j.results || []).find((x: any) => x.previewUrl);
      if (hit) {
        const p: Pista = {
          previewUrl: hit.previewUrl,
          artwork: hit.artworkUrl100 || '',
          artista: hit.artistName || '',
          itunesUrl: hit.trackViewUrl || '',
        };
        setPista(p);
        onPlay(nombre, p);
      } else {
        setSinResultado(true);
      }
    } catch {
      setSinResultado(true);
    }
    setBuscando(false);
  };

  return (
    <motion.li
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.02, 0.4) }}
      className="flex items-center gap-3 rounded-xl border border-transparent px-3 py-2 transition-colors hover:border-white/10 hover:bg-white/[0.03]"
    >
      <span className="w-6 flex-shrink-0 text-center text-xs font-bold tabular-nums text-muted-foreground">{index + 1}</span>

      {pista?.artwork ? (
        <img src={pista.artwork} alt="" className="h-9 w-9 flex-shrink-0 rounded-md object-cover" />
      ) : (
        <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-md" style={{ background: hexToRgba(color, 0.12), color }}>
          <Music2 className="h-4 w-4" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{nombre}</p>
        {pista?.artista && <p className="truncate text-[0.7rem] text-muted-foreground">{pista.artista}</p>}
        {sinResultado && <p className="text-[0.7rem] text-muted-foreground/70">No se ha encontrado grabación</p>}
      </div>

      {pista?.itunesUrl && (
        <a
          href={pista.itunesUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 text-muted-foreground/60 transition-colors hover:text-foreground"
          title="Abrir en iTunes"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}

      <button
        type="button"
        onClick={pulsar}
        disabled={buscando || sinResultado}
        title={sinResultado ? 'Sin grabación disponible' : reproduciendo ? 'Parar' : 'Escuchar'}
        className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full border transition-all disabled:opacity-30"
        style={{ borderColor: hexToRgba(color, 0.4), background: reproduciendo ? color : hexToRgba(color, 0.12), color: reproduciendo ? '#0b0b0b' : color }}
      >
        {buscando ? <Loader2 className="h-4 w-4 animate-spin" /> : reproduciendo ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </button>
    </motion.li>
  );
}

export default function BandaPublicaPage() {
  const { id } = useParams<{ id: string }>();
  const [banda, setBanda] = useState<Banda | null>(null);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [sonando, setSonando] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'bandas', id));
        if (!cancelled) setBanda(snap.exists() ? (sanitizeFirestoreData({ id: snap.id, ...snap.data() }) as Banda) : null);
      } catch {
        if (!cancelled) setBanda(null);
      }
      if (!cancelled) setLoading(false);

      // Igualdad + orden sobre campos distintos exigiría índice compuesto: se
      // ordena en memoria.
      try {
        const ps = await getDocs(query(collection(db, 'social_posts'), where('authorId', '==', id), limit(30)));
        const lista = ps.docs
          .map((d) => sanitizeFirestoreData({ id: d.id, ...d.data() }) as SocialPost)
          .sort((a, b) => new Date(b.createdAt as any).getTime() - new Date(a.createdAt as any).getTime())
          .slice(0, 6);
        if (!cancelled) setPosts(lista);
      } catch {
        if (!cancelled) setPosts([]);
      }
    })();

    return () => { cancelled = true; };
  }, [id]);

  // Un solo <audio> para toda la página: al elegir otra marcha se sustituye.
  useEffect(() => {
    return () => { audioRef.current?.pause(); };
  }, []);

  const reproducir = (nombre: string, pista: Pista) => {
    if (!pista) return;
    audioRef.current?.pause();
    const a = new Audio(pista.previewUrl);
    a.volume = 0.85;
    a.onended = () => setSonando(null);
    a.play().catch(() => setSonando(null));
    audioRef.current = a;
    setSonando(nombre);
  };

  const parar = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setSonando(null);
  };

  const color = banda?.colorPrimario || '#9b8854';
  const color2 = banda?.colorSecundario || '#000000';

  const repertorio = useMemo(() => {
    const lista = (banda?.repertorio || []).filter(Boolean);
    const q = busqueda.trim().toLowerCase();
    return q ? lista.filter((m) => m.toLowerCase().includes(q)) : lista;
  }, [banda, busqueda]);

  const fotos = useMemo(
    () => posts.flatMap((p) => (Array.isArray(p.mediaUrl) ? p.mediaUrl : p.mediaUrl ? [p.mediaUrl] : [])).filter(Boolean).slice(0, 8),
    [posts],
  );

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!banda) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <Music2 className="h-12 w-12 text-muted-foreground/50" />
        <h1 className="font-headline text-2xl font-bold">Esta banda no existe</h1>
        <Button asChild variant="outline"><Link to="/bandas"><ArrowLeft className="h-4 w-4" /> Volver a las bandas</Link></Button>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {banda.foto && <Image src={banda.foto} alt="" className="absolute inset-0 h-full w-full scale-105 object-cover opacity-30" />}
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, hsl(var(--background)), ${hexToRgba(color, 0.25)} 60%, transparent)` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-background/20" />

        <div className="container relative mx-auto px-4 pb-12 pt-24 md:pt-28">
          <Button asChild variant="ghost" size="sm" className="mb-6 -ml-2 text-muted-foreground">
            <Link to="/bandas"><ArrowLeft className="h-4 w-4" /> Todas las bandas</Link>
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="flex flex-col items-center gap-6 text-center md:flex-row md:items-end md:text-left"
          >
            {banda.escudoUrl && (
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 rounded-full blur-2xl" style={{ background: hexToRgba(color, 0.5) }} />
                <div className="relative grid h-32 w-32 place-items-center rounded-3xl border-2 bg-black/40 p-3 backdrop-blur md:h-40 md:w-40" style={{ borderColor: hexToRgba(color, 0.5) }}>
                  <img src={banda.escudoUrl} alt={banda.nombreCompleto} className="h-full w-full object-contain" />
                </div>
              </div>
            )}

            <div className="min-w-0 flex-1">
              <h1 className="font-headline text-4xl font-black leading-tight md:text-6xl">{banda.nombreCompleto}</h1>
              <div className="mt-4 flex flex-wrap justify-center gap-2 md:justify-start">
                <span className="flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold" style={{ borderColor: hexToRgba(color, 0.4), background: hexToRgba(color, 0.15), color }}>
                  <Drum className="h-3 w-3" />
                  {banda.tipo}
                </span>
                {!!banda.fundacion && (
                  <span className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold">
                    <Calendar className="h-3 w-3" />
                    Desde {banda.fundacion}
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${color}, ${color2})` }} />
      </section>

      <div className="container mx-auto grid gap-8 px-4 py-12 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-8">
          {/* ── Repertorio ─────────────────────────────────────────────── */}
          <section>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 font-headline text-xl font-bold">
                <ListMusic className="h-5 w-5" style={{ color }} /> Repertorio
                <span className="text-sm font-normal text-muted-foreground">({(banda.repertorio || []).length})</span>
              </h2>
              {(banda.repertorio || []).length > 6 && (
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar marcha…" className="w-52 pl-9" />
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-white/8 bg-card/40 p-3">
              {repertorio.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {busqueda ? 'Ninguna marcha coincide con la búsqueda.' : 'Esta banda todavía no tiene repertorio registrado.'}
                </p>
              ) : (
                <ul className="space-y-0.5">
                  {repertorio.map((m, i) => (
                    <MarchaRow
                      key={`${m}-${i}`}
                      nombre={m}
                      index={i}
                      color={color}
                      reproduciendo={sonando === m}
                      onPlay={reproducir}
                      onStop={parar}
                    />
                  ))}
                </ul>
              )}
            </div>
            <p className="mt-2 text-[0.7rem] text-muted-foreground">
              Las grabaciones son fragmentos de ~30 s buscados en iTunes. Puede que alguna marcha no esté disponible.
            </p>
          </section>

          {(banda.bio || banda.info) && (
            <section>
              <h2 className="mb-3 flex items-center gap-2 font-headline text-xl font-bold">
                <Music2 className="h-5 w-5" style={{ color }} /> Historia
              </h2>
              <div className="rounded-2xl border border-white/8 bg-card/40 p-5">
                <RichText>{banda.bio || banda.info || ''}</RichText>
              </div>
            </section>
          )}

          {fotos.length > 0 && (
            <section>
              <h2 className="mb-3 flex items-center gap-2 font-headline text-xl font-bold">
                <Drum className="h-5 w-5" style={{ color }} /> Imágenes
              </h2>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {fotos.map((src, i) => (
                  <motion.div key={`${src}-${i}`} whileHover={{ y: -4 }} className="aspect-square overflow-hidden rounded-xl border border-white/8">
                    <Image src={src} alt="" className="h-full w-full object-cover transition-transform duration-500 hover:scale-110" />
                  </motion.div>
                ))}
              </div>
            </section>
          )}

          {posts.length > 0 && (
            <section>
              <h2 className="mb-3 flex items-center gap-2 font-headline text-xl font-bold">
                <Newspaper className="h-5 w-5" style={{ color }} /> Últimas publicaciones
              </h2>
              <div className="space-y-3">
                {posts.slice(0, 4).map((p) => (
                  <article key={p.id} className="rounded-2xl border border-white/8 bg-card/40 p-4">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="font-bold">{p.title || 'Publicación'}</h3>
                      {p.createdAt && (
                        <time className="flex-shrink-0 text-[0.7rem] text-muted-foreground">
                          {format(new Date(p.createdAt as any), "d 'de' MMMM yyyy", { locale: es })}
                        </time>
                      )}
                    </div>
                    <div className="mt-1.5 line-clamp-4 text-sm text-muted-foreground">
                      <RichText>{p.content || ''}</RichText>
                    </div>
                  </article>
                ))}
              </div>
              <Button asChild variant="outline" className="mt-4 w-full">
                <Link to="/social">Ver todo en la red social</Link>
              </Button>
            </section>
          )}
        </div>

        {/* ── Lateral ─────────────────────────────────────────────────────── */}
        <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
          <div className="grid grid-cols-3 gap-2">
            <StatCard icon={<Users className="h-4 w-4" />} value={banda.componentes || '—'} label="Componentes" color={color} />
            <StatCard icon={<ListMusic className="h-4 w-4" />} value={(banda.repertorio || []).length} label="Marchas" color={color} />
            <StatCard icon={<Calendar className="h-4 w-4" />} value={banda.fundacion || '—'} label="Fundación" color={color} />
          </div>

          <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
            <p className="flex items-center gap-2 text-[0.65rem] font-bold uppercase tracking-widest text-muted-foreground">
              <Drum className="h-3.5 w-3.5" style={{ color }} /> Formación
            </p>
            <p className="mt-1.5 text-sm font-semibold">{banda.tipo}</p>
          </div>

          <div
            className="relative overflow-hidden rounded-2xl border p-5 text-center"
            style={{ borderColor: hexToRgba(color, 0.35), background: `linear-gradient(135deg, ${hexToRgba(color, 0.18)}, transparent 70%)` }}
          >
            <p className="text-sm font-bold">¿Quieres tocar con ellos?</p>
            <p className="mt-1 text-xs text-muted-foreground">Entra en Jartiland y únete a una banda desde Jartinet.</p>
            <Button asChild className="mt-3 w-full">
              <Link to="/apply"><DoorOpen className="h-4 w-4" /> Entrar al servidor</Link>
            </Button>
          </div>
        </aside>
      </div>
    </main>
  );
}
